import { logToRecord } from "@latticexyz/store-sync";
import mudConfig from "contracts/mud.config";
import { useEffect, useRef, useState } from "react";
import { ConnectedClient, mockWS } from "./common";
import { Hex, toHex, slice, hexToBytes } from "viem";
import { outbound, submitBatch, processBatch, inbound } from "./batch";
import { intercept } from "mock-socket";
import { storeSync } from "./storeSync";
import { bufferCount, filter } from "rxjs";

type UseConnectChainInput = {
  client: ConnectedClient;
  matchId: number;
  participants: number;
  isHost?: boolean;
};

type Metrics = {
  tps: number;
  messagesIn: number;
  messagesOut: number;
  logs: number;
  batchSize: number;
  roundtrip: number;
};

type UseConnectChainResult = {
  metrics: Metrics;
  txs: { current: { hash: Hex; roundtrip: number }[] };
};

export function useConnectChain({
  client,
  matchId,
  isHost,
  participants,
}: UseConnectChainInput): UseConnectChainResult {
  const messagesIn = useRef(0);
  const messagesOut = useRef(0);
  const txCounter = useRef(0);
  const logCounter = useRef(0);
  const batchSize = useRef(0);
  const roundtrip = useRef(0);
  const txs = useRef<{ hash: Hex; roundtrip: number }[]>([]);
  const [metrics, setMetrics] = useState<Metrics>({
    tps: 0,
    messagesIn: 0,
    messagesOut: 0,
    logs: 0,
    batchSize: 0,
    roundtrip: 0,
  });

  useEffect(() => {
    const updateMetrics = () => {
      setMetrics({
        tps: txCounter.current,
        messagesIn: messagesIn.current,
        messagesOut: messagesOut.current,
        logs: logCounter.current,
        batchSize: batchSize.current,
        roundtrip: Math.ceil(roundtrip.current / txCounter.current),
      });

      txCounter.current = 0;
      messagesIn.current = 0;
      messagesOut.current = 0;
      logCounter.current = 0;
      batchSize.current = 0;
      roundtrip.current = 0;
    };

    const interval = setInterval(() => requestIdleCallback(updateMetrics, { timeout: 1000 }), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    (async () => {
      let ownDoomClientId: Hex | undefined = undefined;
      console.log("I am wallet", client.account?.address);

      const server = intercept(mockWS);

      inbound.subscribe((packet) => {
        server.send(hexToBytes(packet));
        messagesIn.current++;
      });

      server.on("message", (message) => {
        const messageHex = toHex(message);
        ownDoomClientId = slice(messageHex, 4, 8);
        // parsePacket(message);
        outbound.next(messageHex);
        messagesOut.current++;
      });

      const tablesById = Object.fromEntries(Object.values(mudConfig.tables).map((table) => [table.tableId, table]));
      storeSync.storedBlockLogs$.subscribe((block) => {
        const timeBefore = Date.now();
        for (const log of block.logs) {
          logCounter.current++;
          const table = tablesById[log.args.tableId];
          if (!table) {
            continue;
          }
          if (table.label === "DoomMessages" && log.eventName === "Store_SetRecord" && ownDoomClientId) {
            const batch = logToRecord({ table, log });
            if (batch.matchId === matchId) {
              processBatch({ ownDoomClientId, batch });
            }
          }
        }
        console.log("processed batch in", Date.now() - timeBefore);
      });
    })();
  }, []);

  useEffect(() => {
    console.log("I am host", isHost, "for", participants, "participants");
    const subscription = outbound
      .pipe(
        // bufferCount(isHost ? Math.max(participants - 1, 1) : 1),
        bufferCount(1),
        filter((batch) => batch.length > 0),
      )
      .subscribe(async (batch) => {
        batchSize.current = batch.length;
        const result = await submitBatch(client, matchId, batch);
        if (result) {
          txCounter.current++;
          roundtrip.current += result.roundtrip;
          txs.current.push({ hash: result.txHash, roundtrip: result.roundtrip });
        }
      });

    return () => subscription.unsubscribe();
  }, [participants, isHost]);

  return { metrics, txs };
}
